import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import StyledInput from '@/components/styled/StyledInput';
import StyledSelect from '@/components/styled/StyledSelect';
import { Label } from '@/components/ui/label';
import SearchIcon from '@/assets/icons/search.svg?react';
import { useGetHotelAssociatedBenefits } from '@/hooks/useBenefits';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import type { HotelAssociatedBenefitItem } from '@/api/benefit.api';
import { getBenefitTypeTranslatedLabel } from '@/utils/benefits/getBenefitTypeName';
import { BenefitType } from '@/types/benefits/benefitTypes';
import type { AssociatedBenefitFrequency } from '@/types/benefits/associated-benefit-frequency';
import StyledButton from '@/components/styled/StyledButton';
import StyledRadioGroup from '@/components/styled/StyledRadioGroup';
import Modal from '@/components/ui/Modal';
import StyledTable from '@/components/styled/StyledTable';
import { HOTEL_ASSOCIATED_BENEFIT_SELECTION_COLUMNS } from '@/types/benefits/hotelAssociatedBenefitColumnDef';

export type AssociatedBenefitDisplay = {
  id: string;
  name: string | null;
  code: number | null;
  type: string;
  country: string | null;
  supplier: { id: string; name: string | null } | null;
  activity: { activityType: string; city: string | null } | null;
};

interface HotelAssociatedBenefitAdditionModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (
    benefitId: string,
    frequency: AssociatedBenefitFrequency,
    benefit: AssociatedBenefitDisplay,
  ) => void;
  existingBenefitIds: string[];
  isViewMode?: boolean;
}

type FiltersForm = {
  search: string;
  supplierId?: { value: string; label: string };
  country?: { value: string; label: string };
  activityType?: { value: string; label: string };
};

const HotelAssociatedBenefitAdditionModal = ({
  open,
  onClose,
  onAdd,
  existingBenefitIds,
  isViewMode = false,
}: HotelAssociatedBenefitAdditionModalProps) => {
  const { t } = useTranslation();
  const [selectedBenefit, setSelectedBenefit] = useState<{
    benefit: HotelAssociatedBenefitItem;
    benefitDisplay: AssociatedBenefitDisplay;
  } | null>(null);

  const { control } = useForm<FiltersForm>({
    defaultValues: {
      search: '',
      supplierId: undefined,
      country: undefined,
      activityType: undefined,
    },
  });

  const search = useWatch({ control, name: 'search' });
  const supplierId = useWatch({ control, name: 'supplierId' });
  const country = useWatch({ control, name: 'country' });
  const activityTypeFilter = useWatch({ control, name: 'activityType' });
  const debouncedSearch = useDebouncedSearch(search);

  const { data } = useGetHotelAssociatedBenefits(
    {
      name: debouncedSearch || undefined,
      supplierId: supplierId?.value,
      country: country?.value,
      activityType: activityTypeFilter?.value,
    },
    ({ benefits, filters }) => ({
      benefits,
      suppliers: filters.suppliers.map((s) => ({
        value: s.id,
        label: s.name ?? '',
      })),
      countries: filters.countries.map((c) => ({
        value: c.country,
        label: c.country,
      })),
      activityTypes: filters.activityTypes.map((value) => ({
        value,
        label:
          value === 'ACTIVITY'
            ? getBenefitTypeTranslatedLabel(t, BenefitType.ACTIVITY)
            : value === 'TRANSFER'
              ? getBenefitTypeTranslatedLabel(t, BenefitType.TRANSFER)
              : value,
      })),
    }),
  );

  const benefits = data?.benefits ?? [];
  const supplierOptions = data?.suppliers ?? [];
  const countryOptions = data?.countries ?? [];
  const activityTypeOptions = data?.activityTypes ?? [];

  const getTranslatedConst = useTranslatedConst();

  const handleRowClick = (benefit: HotelAssociatedBenefitItem) => {
    if (existingBenefitIds.includes(benefit.id)) return;
    setSelectedBenefit({
      benefit,
      benefitDisplay: benefit as AssociatedBenefitDisplay,
    });
  };

  const handleClose = () => {
    setSelectedBenefit(null);
    onClose();
  };

  const benefitSelectionColumns = getTranslatedConst(
    HOTEL_ASSOCIATED_BENEFIT_SELECTION_COLUMNS,
  );

  const frequencyForm = useForm<{ frequency: AssociatedBenefitFrequency }>({
    defaultValues: { frequency: 'EACH_NIGHT' },
  });

  if (!open) return null;

  return (
    <Modal
      closeModal={handleClose}
      title={t('hotels.associatedBenefits.addBenefit')}
      actionTitle=""
      actionOnClick={() => {}}
      hasAction={false}
      message=""
    >
      <div className="flex flex-col gap-4 overflow-auto max-h-[70vh] min-w-[520px]">
        <StyledInput
          control={control}
          name="search"
          placeholder={t('circuits.editorial.searchPlaceholder')}
          title={t('circuits.editorial.searchTitle')}
          icon={<SearchIcon className="size-5 stroke-gray-500" />}
        />
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('benefits.list.filterBy')}
          </span>
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[180px]">
              <StyledSelect
                control={control}
                name="supplierId"
                options={supplierOptions}
                title={t('hotels.supplier')}
                placeholder={t('hotels.selectSupplier')}
                isClearable
                disabled={isViewMode}
              />
            </div>
            <div className="min-w-[180px]">
              <StyledSelect
                control={control}
                name="country"
                options={countryOptions}
                title={t('hotels.country')}
                placeholder={t('hotels.selectCountry')}
                isClearable
                disabled={isViewMode}
              />
            </div>
            <div className="min-w-[140px]">
              <StyledSelect
                control={control}
                name="activityType"
                options={activityTypeOptions}
                title={t('hotels.associatedBenefits.type')}
                placeholder={t('common.all')}
                isClearable
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>
        <div className="border rounded-md overflow-auto max-h-[280px]">
          {benefits.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {t('hotels.associatedBenefits.noBenefits')}
            </div>
          ) : (
            <StyledTable<HotelAssociatedBenefitItem, unknown>
              columns={benefitSelectionColumns}
              data={benefits}
              getRowId={(row) => row.id}
              onRowClick={(row) => {
                if (!existingBenefitIds.includes(row.original.id)) {
                  handleRowClick(row.original);
                }
              }}
              enablePagination={false}
            />
          )}
        </div>
        {selectedBenefit && (
          <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-md">
            <Label className="font-medium">
              {t('hotels.associatedBenefits.selectedBenefit')}:{' '}
              {selectedBenefit.benefitDisplay.name}
            </Label>
            <div>
              <Label className="text-gray-700 block mb-1">
                {t('hotels.associatedBenefits.frequency')}
              </Label>
              <StyledRadioGroup
                control={frequencyForm.control}
                name="frequency"
                options={[
                  {
                    label: t('hotels.associatedBenefits.frequencyOnce'),
                    value: 'ONCE',
                  },
                  {
                    label: t('hotels.associatedBenefits.frequencyEachNight'),
                    value: 'EACH_NIGHT',
                  },
                ]}
                disabled={isViewMode}
              />
            </div>
            <div className="flex gap-2">
              <StyledButton
                variant="outlined"
                title={t('common.cancel')}
                onClick={() => setSelectedBenefit(null)}
              />
              <StyledButton
                title={t('hotels.associatedBenefits.addToList')}
                onClick={() => {
                  const freq = frequencyForm.getValues('frequency');
                  if (selectedBenefit && freq) {
                    onAdd(
                      selectedBenefit.benefit.id,
                      freq,
                      selectedBenefit.benefitDisplay,
                    );
                    setSelectedBenefit(null);
                    frequencyForm.reset({ frequency: 'ONCE' });
                    onClose();
                  }
                }}
                disabled={isViewMode}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HotelAssociatedBenefitAdditionModal;
